import { createHttpClient } from '@adapters/HttpClient';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RatesApiAdapter } from '../adapters/RatesApiAdapter';
import { fetchAllActiveOwnBoxes } from './useActiveOwnBoxes';
import { generateRatesComparisonPdf } from '../utils/generateRatesComparisonPdf';
import { RatePdfLabels } from '../utils/ratePdfLabels';

export const useGenerateRatesPdf = () => {
  const { t } = useTranslation('ratesModule');
  const { toast } = useToast();
  const currency = useCurrency();
  const [isGenerating, setIsGenerating] = useState(false);

  const labels: RatePdfLabels = useMemo(
    () => ({
      sections: {
        shipping: t('itemTypes.shipping'),
        fulfillment: t('itemTypes.fulfillment'),
        storage: t('itemTypes.storage'),
        packaging: t('itemTypes.packaging'),
      },
      shipping: {
        courier: t('form.shipping.courier'),
        service: t('form.shipping.service'),
        country: t('form.shipping.country'),
        zone: t('form.shipping.zone'),
        weightRanges: t('itemData.shipping.weightRanges'),
        returnPolicy: t('itemData.shipping.returnShippingPolicy'),
        returnPolicySamePrice: t('itemData.shipping.returnPolicySamePrice'),
        returnPolicyFree: t('itemData.shipping.returnPolicyFree'),
        returnPolicyCustom: t('itemData.shipping.returnPolicyCustom'),
        returnWeightRanges: t('itemData.shipping.returnWeightRanges'),
        codFees: t('itemData.shipping.codFees'),
        fixedFee: t('itemData.shipping.fixedFee'),
        percentageFee: t('itemData.shipping.percentageFee'),
        extraCharges: t('itemData.shipping.extraCharges'),
        fuelSurcharge: t('itemData.shipping.fuelSurcharge'),
        extraWeightCharge: t('itemData.shipping.extraWeightCharge'),
        minWeight: t('form.shipping.minWeight'),
        maxWeight: t('form.shipping.maxWeight'),
        amount: t('form.shipping.amount', { currency }),
        weightRangeColumn: t('pdf.weightRangeColumn'),
      },
      fulfillment: {
        packingFee: t('itemData.fulfillment.packingFee'),
        refusedFee: t('itemData.fulfillment.refusedFee'),
        fixedFee: t('itemData.fulfillment.fixedFee'),
        extraLinesFee: t('itemData.fulfillment.extraLinesFee'),
        sinceLineNumber: t('itemData.fulfillment.sinceLineNumber'),
        attachmentFee: t('itemData.fulfillment.attachmentFee'),
      },
      storage: {
        fixedFee: t('itemData.storage.fixedFee'),
      },
      packaging: {
        box: t('sections.packagingPrices.columns.name'),
        price: t('sections.packagingPrices.columns.ratePrice', { currency }),
      },
      noData: t('itemData.noData'),
      notApplicable: t('pdf.notApplicable'),
    }),
    [t, currency],
  );

  const generatePdf = useCallback(
    async (rateIds: number[]) => {
      if (rateIds.length === 0) return;

      setIsGenerating(true);

      const previewWindow = window.open('', '_blank');
      if (!previewWindow) {
        toast({
          title: t('pdf.popupBlocked'),
          variant: 'destructive',
        });
        setIsGenerating(false);
        return;
      }

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new RatesApiAdapter(httpClient);

        const [rates, ownBoxes] = await Promise.all([
          Promise.all(rateIds.map((id) => adapter.getRateById(id, false))),
          fetchAllActiveOwnBoxes(),
        ]);

        await generateRatesComparisonPdf({
          rates,
          ownBoxes,
          currency,
          labels,
          previewWindow,
        });

        toast({
          title: t('pdf.success'),
        });
      } catch (error: unknown) {
        if (!previewWindow.closed) {
          previewWindow.close();
        }

        const message =
          error instanceof Error && error.message === 'POPUP_BLOCKED'
            ? t('pdf.popupBlocked')
            : error instanceof Error
              ? error.message
              : undefined;

        toast({
          title: t('pdf.error'),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsGenerating(false);
      }
    },
    [currency, labels, t, toast],
  );

  return {
    generatePdf,
    isGenerating,
  };
};
